import {FlexColumn, Link, Placeholder} from '@forecast-it/design-system';
import {CalendarEvent} from './CalendarEvent';
import React, {useEffect, useState} from 'react';
import {CalendarEventType} from './CalendarEventsFetch';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {getCalendarEventSize} from './CalendarEventUtil';
import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../../containers/event_manager';
import {LinkWrapper} from '../TimesheetsCalendar_styled';
import {isEventConvertedType} from './useConvertedEventTracking';

export type CalendarEventsProps = {
	calendarEvents: CalendarEventType[];
	isEventConverted: isEventConvertedType;
	renderPlaceholders: boolean;
	isLocked: boolean;
	openModal: (calendarEvent: CalendarEventType) => void;
	showContextMenu: (mouseEvent: MouseEvent, calendarEvent: CalendarEventType) => void;
};

type EventArgs = {
	number: number;
};

export const CalendarEvents = ({
	calendarEvents,
	isEventConverted,
	renderPlaceholders,
	isLocked,
	openModal,
	showContextMenu,
}: CalendarEventsProps): React.JSX.Element => {
	const defaultVisibleEvents = 4;
	const maxEventSizeTotal = 228;
	const [visibleEvents, setVisibleEvents] = useState(defaultVisibleEvents);
	const visibleCalendarEvents = calendarEvents?.slice(0, visibleEvents);
	const numberOfHiddenEvents = calendarEvents?.length - visibleEvents;
	const [totalEventSizeLargerThanMax, setTotalEventSizeLargerThanMax] = useState(false);
	const showShowLessButton = calendarEvents?.length > 0 && totalEventSizeLargerThanMax && numberOfHiddenEvents === 0;

	const hideEvents = () => {
		if (calendarEvents) {
			let eventSizeTotal = 0;
			let numOfVisibleItems = 0;
			for (let i = 0; i < calendarEvents.length; i++) {
				const event = calendarEvents[i];
				eventSizeTotal += getCalendarEventSize(event);
				if (eventSizeTotal > maxEventSizeTotal) {
					setTotalEventSizeLargerThanMax(true);
					break;
				}
				numOfVisibleItems++;
			}
			setVisibleEvents(numOfVisibleItems);
		} else {
			setVisibleEvents(defaultVisibleEvents);
		}
	};

	const handleShowMore = (args: EventArgs) => {
		setVisibleEvents(visibleEvents + args.number);
	};

	const handleShowLess = () => {
		hideEvents();
	};

	useEffect(() => {
		subscribe(EVENT_ID.CALENDAR_EVENTS_SHOW_MORE, handleShowMore);
		return () => {
			unsubscribe(EVENT_ID.CALENDAR_EVENTS_SHOW_MORE, handleShowMore);
		};
	}, [handleShowMore]);

	useEffect(() => {
		subscribe(EVENT_ID.CALENDAR_EVENTS_SHOW_LESS, handleShowLess);
		return () => {
			unsubscribe(EVENT_ID.CALENDAR_EVENTS_SHOW_LESS, handleShowLess);
		};
	}, [handleShowLess]);

	const showLess = (e: React.MouseEvent) => {
		e.stopPropagation();
		trackEvent('Show Less Events Link', 'Clicked');
		dispatch(EVENT_ID.CALENDAR_EVENTS_SHOW_LESS);
	};
	const showMoreEvents = (e: React.MouseEvent, number: number) => {
		e.stopPropagation();
		trackEvent('Show More Events Link', 'Clicked', {totalEvents: calendarEvents?.length});
		dispatch(EVENT_ID.CALENDAR_EVENTS_SHOW_MORE, {number: number});
	};

	useEffect(() => {
		hideEvents();
	}, [calendarEvents?.length]);

	return (
		<>
			<FlexColumn gap="none">
				{visibleCalendarEvents?.length ? (
					visibleCalendarEvents.map(event => (
						<CalendarEvent
							key={'calendar-event-key' + event?.id}
							event={event}
							isLocked={isLocked}
							isConverted={isEventConverted(event?.id)}
							openModal={openModal}
							showContextMenu={showContextMenu}
						/>
					))
				) : renderPlaceholders ? (
					<>
						{[...Array(4)].map((value, index) => (
							<Placeholder key={'placeholder-key-' + index} height={'52px'} />
						))}
					</>
				) : null}
			</FlexColumn>
			{numberOfHiddenEvents > 0 && (
				<LinkWrapper>
					<Link size={'2'} onClick={e => showMoreEvents(e, numberOfHiddenEvents)}>
						Show {numberOfHiddenEvents} more
					</Link>
				</LinkWrapper>
			)}
			{!!showShowLessButton && (
				<LinkWrapper>
					<Link size={'2'} onClick={e => showLess(e)}>
						Show less
					</Link>
				</LinkWrapper>
			)}
		</>
	);
};
